import { Button } from '@ahafood/ui/components/button';
import { Card, CardContent } from '@ahafood/ui/components/card';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { cn } from '@ahm/common-helpers';
import { getCookie } from 'cookies-next';
import { ArrowRight, Star, User } from 'lucide-react';
import React, { useMemo } from 'react';

import { RatingDistribution } from './rating-distribution';
import { useRatingSheet, RatingSheetProvider } from './rating-sheet-context';
import type { Store } from '@/api/ahafood/get-store-fn';
import {
  useGetStoreRating,
  type MyRating,
  type OtherRating,
} from '@/api/use-get-rating';
import { formatUnixTime, DateFormat } from '@/lib/datetime';

type RatingPreviewProps = {
  store: Store;
  mode?: 'detail' | 'preview';
};

export function StoreRatingPreview({ store, mode }: RatingPreviewProps) {
  const { openSheet } = useRatingSheet();
  const token = String(getCookie('ws_token') ?? '');

  const { data: ratingsData, isLoading } = useGetStoreRating(
    token,
    {
      store_id: store._id,
      limit: 20,
      skip: 0,
    },
    {
      enabled: !!store._id,
    }
  );

  // Combine all ratings and get first 5 for preview
  const allRatings = useMemo(() => {
    if (!ratingsData) return [];
    return [...ratingsData.my_ratings, ...ratingsData.other_ratings];
  }, [ratingsData]);

  const previewRatings = useMemo(() => {
    return allRatings.slice(0, 5);
  }, [allRatings]);

  const handleOpenRatings = () => {
    // Pass the store data and loaded ratings
    openSheet(store, allRatings, {});
  };

  // eslint-disable-next-line sonarjs/no-identical-functions
  const handleViewAll = () => {
    openSheet(store, allRatings, {
      required_text: true,
    });
  };

  if (!store.rating || store.rating.total === 0) {
    return null;
  }

  return (
    <Card className="w-full border-none shadow-none">
      <CardContent className={cn('w-full', mode === 'preview' ? 'p-4' : 'p-0')}>
        {/* Header Section */}
        {mode === 'preview' ? (
          <div
            className="flex items-center justify-between border-b pb-2 mb-4"
            data-testid="store-detail-preview-section"
          >
            <h2
              className="text-lg font-bold"
              data-testid="store-detail-preview-rating-title"
            >
              Đánh giá từ khách hàng
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewAll}
              className="text-primary hover:text-primary/80 [&_svg]:size-5 [&_svg]:text-neutral-90"
              data-testid="store-detail-preview-rating-arrow"
            >
              <ArrowRight />
            </Button>
          </div>
        ) : (
          <RatingDistribution store={store} />
        )}

        {/* Recent Reviews Preview */}
        <div className="space-y-3 w-full">
          {isLoading ? (
            <div className="w-full">
              <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="flex-none min-w-0"
                    style={{ width: '280px' }}
                  >
                    <PreviewRatingSkeleton />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            previewRatings.length > 0 && (
              <div className="w-full max-w-[calc(100vw-2rem)] flex gap-3 overflow-x-auto scrollbar-hide pb-2">
                {previewRatings.map((rating, index) => (
                  <div
                    key={`${rating.order_code}-${index}`}
                    className="flex-none w-[75vw] md:w-80"
                  >
                    <PreviewRatingCard
                      rating={rating}
                      onClick={handleOpenRatings}
                    />
                  </div>
                ))}
              </div>
            )
          )}
          {mode === 'detail' && (
            <div className="w-full mx-auto px-2">
              <Button
                onClick={handleViewAll}
                className="w-full text-primary-40 border-primary-40 hover:text-primary-50"
                variant="outline"
              >
                Hiển thị tất cả đánh giá
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Preview Rating Card Component
function PreviewRatingCard({
  rating,
  onClick,
}: {
  rating: MyRating | OtherRating;
  onClick: () => void;
}) {
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const today = new Date();

    // So sánh phần yyyy-mm-dd
    const isToday = date.toDateString() === today.toDateString();

    return formatUnixTime(
      timestamp,
      isToday ? DateFormat.TIME_24 : DateFormat.DATE
    );
  };

  return (
    <Card
      className="cursor-pointer size-full border hover:shadow-sm transition-shadow overflow-hidden"
      onClick={onClick}
      data-testid="store-detail-preview-rating-card"
    >
      <CardContent className="p-3 space-y-3 w-full overflow-hidden">
        {/* User Info */}
        <div className="flex items-start gap-2">
          <div className="size-8 rounded-full bg-neutral-10 flex items-center justify-center">
            <User className="size-4 text-neutral-50" />
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <div
              className="text-sm font-medium truncate"
              data-testid="store-detail-preview-rating-name"
            >
              {rating.customer_name || 'Người dùng'}
            </div>
            <div
              className="flex items-center gap-1"
              data-testid="store-detail-preview-rating-star"
            >
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={cn(
                    'size-3 shrink-0',
                    star <= rating.overall
                      ? 'fill-yellow-40 text-yellow-40'
                      : 'fill-neutral-30 text-neutral-30'
                  )}
                />
              ))}
              <span className="text-xs text-muted-foreground ml-1 truncate">
                {formatTime(rating.create_at)}
              </span>
            </div>
          </div>
        </div>

        {rating.comment && (
          <div className="text-sm text-neutral-90 line-clamp-1 leading-relaxed break-words">
            {rating.comment}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Preview Rating Skeleton Component
function PreviewRatingSkeleton() {
  return (
    <Card className="bg-pink-50 border-neutral-10">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="size-8 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-3 w-20 mb-1" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
        <div className="space-y-1">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <div className="flex gap-1">
          <Skeleton className="size-12 rounded" />
          <Skeleton className="size-12 rounded" />
          <Skeleton className="size-12 rounded" />
        </div>
      </CardContent>
    </Card>
  );
}

// Don't forget to wrap your app/page with RatingSheetProvider
export function PageWithRatingSheet({
  children,
}: {
  children: React.ReactNode;
}) {
  return <RatingSheetProvider>{children}</RatingSheetProvider>;
}
